import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  ACTIVITY_TYPE_INVOICE_PENDING,
} from '@models/activity.model';
import { FeedSkeletonList } from '@components/organisms/FeedSkeletonList';
import { ActivityCardSkeleton } from '@components/molecules/ActivityCardSkeleton';
import { useMinimumLoading } from '@hooks/useMinimumLoading';
import { ActivityNote } from './ActivityNote';
import { ActivityBudgetRequestAccepted } from './ActivityBudgetRequestAccepted';
import { ActivityBudgetRequestCreated } from './ActivityBudgetRequestCreated';
import { ActivityBudgetRequestSubmitted } from './ActivityBudgetRequestSubmitted';
import { ActivityBudgetRequestRejected } from './ActivityBudgetRequestRejected';
import { ActivityTaskStatus } from './ActivityTaskStatus';
import { ActivityDelegationCanceled } from './ActivityDelegationCanceled';
import { ActivityIncident } from './ActivityIncident';
import { ActivityGeofence } from './ActivityGeofence';
import { ActivityCommunication } from './ActivityCommunication';
import { ActivityPunchClock } from './ActivityPunchClock';
import { ActivityInvoicePending } from './ActivityInvoicePending';
import { FC } from 'react';
import { Incident } from '@models/incident.model';
import { useIncident } from '@hooks/api';
import AcknowledgeIncidentModal from '../../../../components/organisms/AcknowledgeIncidentModal';
import { groupActivitiesByDate } from '@utils/formatDate';
import { useFeatureAccess } from '@utils/featureFlag';
import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import { ActivityBudgetProposalAccepted } from './ActivityBudgetProposalAccepted';
import { ActivityBudgetProposalCountered } from './ActivityBudgetProposalCountered';
import { ActivityCrewAssign } from './ActivityCrewAssign';
import { useTranslation } from '@hooks/useTranslation';

export interface PaginationActions {
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  isFetchingMore?: boolean;
}

interface ProjectFeedProps {
  activities: Activity[];
  paginationActions: PaginationActions;
  setCountFeed?: (count: number) => void;
  handleOpenTaskDetail?: (taskId: string) => void;
  onProjectClick?: (projectId: string) => void;
  totalCount?: number;
  // Read-only viewers (e.g. reviewers) cannot acknowledge incidents
  readOnly?: boolean;
}

export const ProjectFeed: FC<ProjectFeedProps> = ({
  activities,
  paginationActions,
  setCountFeed = () => {},
  handleOpenTaskDetail,
  onProjectClick,
  totalCount,
  readOnly = false,
}) => {
  const tActivityFeed = useTranslation('activityFeed');
  const { loadMore, hasMore, isLoading, isFetchingMore = false } = paginationActions;
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(
    null,
  );
  const [isIncidentModalOpen, setIsIncidentModalOpen] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const { acknowledgeIncident } = useIncident();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef(loadMore);

  // Keep the ref up to date with the latest loadMore function
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  // Hold the initial loading state for a short floor so a fast response doesn't
  // make the skeleton flash imperceptibly.
  const showInitialLoading = useMinimumLoading(isLoading && activities.length === 0);

  // Infinite-scroll sentinel as a CALLBACK REF: it re-creates and re-attaches the
  // observer every time the sentinel node mounts, so we never end up observing a
  // detached node. (The previous effect only re-armed on activities.length, which
  // could miss the live sentinel and silently stop the scroll.)
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && entry.intersectionRatio > 0) {
          loadMoreRef.current();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  // Disconnect the observer on unmount.
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  // Set count feed when totalCount changes
  useEffect(() => {
    if (typeof totalCount === 'number') {
      setCountFeed(totalCount);
    }
  }, [totalCount, setCountFeed]);

  const { canAccess } = useFeatureAccess();
  const canInvoice = canAccess('INVOICES');

  // INVOICE_PENDING activities flow through the normal chronological feed like any
  // other activity — they are not pinned. Use the feed filters to surface them.
  // When the INVOICES feature flag is off, drop INVOICE_PENDING entries entirely
  // (resolved or not) so they don't appear in the feed.
  const feedActivities = useMemo(() => {
    if (canInvoice) return activities;
    return activities.filter((a) => a.type !== ACTIVITY_TYPE_INVOICE_PENDING);
  }, [activities, canInvoice]);

  const groupedActivities = useMemo(() => {
    return groupActivitiesByDate(feedActivities);
  }, [feedActivities]);

  const getActivityLabel = (activity: Activity) => {
    switch (activity.type) {
      case 'Note':
        return (
          <ActivityNote
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'Task Status':
        return (
          <ActivityTaskStatus
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'Incident':
      case 'Incident Acknowledgment':
        return (
          <ActivityIncident
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
            onIncidentAcknowledge={readOnly ? undefined : handleIncidentAcknowledge}
          />
        );
      case 'Geofence':
        return (
          <ActivityGeofence
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
            onProjectClick={onProjectClick}
          />
        );
      case 'Communication':
        return (
          <ActivityCommunication
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'PunchClock':
        return (
          <ActivityPunchClock
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'BUDGET_REQUEST_ACCEPTED':
        return (
          <ActivityBudgetRequestAccepted
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'BUDGET_PROPOSAL_ACCEPTED':
        return (
          <ActivityBudgetProposalAccepted
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'BUDGET_REQUEST_CREATED':
        return (
          <ActivityBudgetRequestCreated
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'BUDGET_PROPOSAL_SUBMITTED':
        return (
          <ActivityBudgetRequestSubmitted
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'BUDGET_REQUEST_REJECTED':
        return (
          <ActivityBudgetRequestRejected
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'TASK_DELEGATION_CANCELLED':
        return (
          <ActivityDelegationCanceled
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'BUDGET_PROPOSAL_COUNTERED':
        return (
          <ActivityBudgetProposalCountered
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case 'CREW_ASSIGN':
        return (
          <ActivityCrewAssign
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      case ACTIVITY_TYPE_INVOICE_PENDING:
        return (
          <ActivityInvoicePending
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
      default:
        return (
          <ActivityNote
            activity={activity}
            handleOpenTaskDetail={handleOpenTaskDetail}
          />
        );
    }
  };

  // Left accent color per activity type (visual only — mirrors the feed prototype).
  const accentClass = (type?: string) => {
    if (type === 'Incident' || type === 'Incident Acknowledgment') return 'bg-red-500';
    if (type === ACTIVITY_TYPE_INVOICE_PENDING) return 'bg-blue-500';
    if (type?.startsWith('BUDGET_')) return 'bg-orange-500';
    return 'bg-slate-300';
  };

  // Wraps a card with a colored left accent bar + entrance animation + hover lift.
  const renderActivityCard = (activity: Activity, keyPrefix = '') => (
    <motion.div
      key={`${keyPrefix}${activity.activityId}-${activity.organizationId || 'org'}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="relative w-full transition-transform duration-200 hover:-translate-y-0.5"
    >
      <span
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1.5 rounded-l-md z-10',
          accentClass(activity.type),
        )}
        aria-hidden="true"
      />
      {getActivityLabel(activity)}
    </motion.div>
  );

  const handleIncidentAcknowledge = (incident: Incident) => {
    setSelectedIncident(incident);
    setIsIncidentModalOpen(true);
  };

  const handleAcknowledgeConfirm = async () => {
    if (selectedIncident) {
      setIsAcknowledging(true);
      try {
        const result = await acknowledgeIncident(selectedIncident.incidentId);
        if (result) {
          setIsIncidentModalOpen(false);
          setSelectedIncident(null);
          // Note: Parent component will handle refreshing activities
        }
      } finally {
        setIsAcknowledging(false);
      }
    }
  };

  const handleModalClose = () => {
    if (!isAcknowledging) {
      setIsIncidentModalOpen(false);
      setSelectedIncident(null);
    }
  };

  // Loading state for initial load — skeleton list instead of a bare spinner.
  if (showInitialLoading) {
    return <FeedSkeletonList />;
  }

  // Empty state
  if (!isLoading && activities.length === 0) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-400">{tActivityFeed.noActivitiesToDisplay}</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative flex flex-col gap-6 w-full max-w-3xl mx-auto">
      {groupedActivities.map(({ dateHeader, activities: dateActivities }) => (
        <motion.div
          key={dateHeader}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full"
        >
          <div className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 py-2 mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                {dateHeader}
              </h2>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
          {/* Slight horizontal inset so the full-width sticky date strip fully
              covers each card's 1px ring/shadow when it scrolls underneath
              (otherwise the ring peeks out as thin lines on the sides). */}
          <div className="flex flex-col gap-4 px-1">
            {dateActivities.map((activity) => renderActivityCard(activity))}
          </div>
        </motion.div>
      ))}

      {/* Loading indicator for pagination — a skeleton card reads as "more coming". */}
      {isFetchingMore && activities.length > 0 && <ActivityCardSkeleton />}

      {/* Sentinel for infinite scroll. Kept mounted while there are more pages
          (even while fetching) so the IntersectionObserver never loses its target. */}
      {hasMore && activities.length > 0 && (
        <div
          ref={sentinelRef}
          className="h-1 w-full bg-transparent"
          aria-hidden="true"
          data-testid="infinite-scroll-sentinel"
        />
      )}

      {/* End of list indicator */}
      {!isLoading && !hasMore && activities.length > 0 && (
        <div className="py-4 text-center text-sm text-gray-400">
          {tActivityFeed.noMoreActivities}
        </div>
      )}

      <AcknowledgeIncidentModal
        open={isIncidentModalOpen}
        onClose={handleModalClose}
        incident={selectedIncident}
        isAcknowledging={isAcknowledging}
        onConfirm={handleAcknowledgeConfirm}
      />
    </div>
  );
};
